/**
 * What the scenarios panel has open, kept in the URL beside the SOP so a reload or a shared link reopens
 * it: the type, its scenarios view, the scenario read there, and the dataset its picks are for. A protocol
 * opens on its first type, so the detail and the lit path are never empty; a type the URL names always wins.
 */

import { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router';

export const useTypeParams = (firstTypeId: string | null) => {
	const [searchParams, setSearchParams] = useSearchParams();
	const selectedTypeId = searchParams.get('type');
	const selectedScenarioId = searchParams.get('scenario');
	// Reading a type and picking a scenario to run under it are different jobs, so the scenarios are their
	// own step rather than where selecting a type lands.
	const viewingScenarios = searchParams.get('view') === 'scenarios' || Boolean(selectedScenarioId);
	// Arrived from a bulk run to change what it ran.
	const reselectRunId = searchParams.get('reselect');
	// Arrived from a dataset to add cases to it. Kept through every move below: the picking lasts until
	// the cases are added or the reader leaves for the dataset.
	const pickingForDatasetId = searchParams.get('dataset');

	// Merged rather than replaced, since the page keys the SOP off the same query string, and replaced in
	// history: moving between levels is not a page of its own.
	const update = useCallback(
		(change: (next: URLSearchParams) => void) =>
			setSearchParams(
				(prev) => {
					const next = new URLSearchParams(prev);
					change(next);
					return next;
				},
				{ replace: true }
			),
		[setSearchParams]
	);

	const setSelectedTypeId = useCallback(
		(id: string | null) =>
			update((next) => {
				if (id) next.set('type', id);
				else next.delete('type');
				// The open scenario, and the scenarios view itself, belonged to the type being left.
				next.delete('scenario');
				next.delete('view');
			}),
		[update]
	);

	const setViewingScenarios = useCallback(
		(viewing: boolean) =>
			update((next) => {
				if (viewing) next.set('view', 'scenarios');
				else {
					next.delete('view');
					next.delete('scenario');
				}
			}),
		[update]
	);

	// One update, not a type then a view: each update starts from the URL as rendered, so the second would
	// undo the first.
	const openScenarios = useCallback(
		(id: string) =>
			update((next) => {
				next.set('type', id);
				next.set('view', 'scenarios');
				next.delete('scenario');
			}),
		[update]
	);

	const setSelectedScenarioId = useCallback(
		(id: string | null) =>
			update((next) => {
				if (id) next.set('scenario', id);
				else next.delete('scenario');
			}),
		[update]
	);

	const clearReselect = useCallback(() => update((next) => next.delete('reselect')), [update]);
	const stopPicking = useCallback(() => update((next) => next.delete('dataset')), [update]);

	useEffect(() => {
		if (firstTypeId && !selectedTypeId) setSelectedTypeId(firstTypeId);
	}, [firstTypeId, selectedTypeId, setSelectedTypeId]);

	return {
		selectedTypeId,
		selectedScenarioId,
		viewingScenarios,
		reselectRunId,
		pickingForDatasetId,
		setSelectedTypeId,
		setViewingScenarios,
		openScenarios,
		setSelectedScenarioId,
		clearReselect,
		stopPicking
	};
};
